// Categorias do blog — dono de `blog_categories`. A contagem de posts por
// categoria vem junto (segunda query leve) porque o único consumidor que
// lista categorias também mostra a contagem.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  created_at: string;
}

export interface BlogCategoryInput {
  name: string;
  slug: string;
  description: string | null;
}

export function useBlogCategories() {
  return useQuery({
    queryKey: ["blog-categories"],
    queryFn: async () => {
      const [{ data: categories, error }, { data: posts }] = await Promise.all([
        supabase.from("blog_categories").select("*").order("name"),
        supabase.from("blog_posts").select("category_id"),
      ]);
      if (error) throw error;
      const postCounts: Record<string, number> = {};
      for (const p of posts ?? []) {
        if (p.category_id) postCounts[p.category_id] = (postCounts[p.category_id] ?? 0) + 1;
      }
      return { categories: (categories ?? []) as BlogCategory[], postCounts };
    },
  });
}

function useInvalidateCategories() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ["blog-categories"] });
    qc.invalidateQueries({ queryKey: ["content-hub-counts"] });
  };
}

export function useCreateBlogCategory() {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: async (input: BlogCategoryInput) => {
      const { error } = await supabase.from("blog_categories").insert(input);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateBlogCategory() {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: BlogCategoryInput }) => {
      const { error } = await supabase.from("blog_categories").update(input).eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteBlogCategory() {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("blog_categories").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });
}
